export type PhoneChannel = 'call' | 'sms' | 'whatsapp' | 'signal' | 'threema';

export type PhoneAction = {
  id: PhoneChannel;
  label: string;
  href: string;
};

export type PhoneExtra = {
  name?: string;
  selected?: number;
};

/** Digits only, matching iOS CharacterSet.decimalDigits filter. */
export function phoneDigits(raw: string): string {
  return raw.replace(/\D/g, '');
}

export function telHref(raw: string): string {
  const trimmed = raw.trim();
  const digits = phoneDigits(trimmed);
  if (!digits) return '';
  return trimmed.includes('+') ? `tel:+${digits}` : `tel:${digits}`;
}

export function smsHref(raw: string): string {
  const trimmed = raw.trim();
  const digits = phoneDigits(trimmed);
  if (!digits) return '';
  return trimmed.includes('+') ? `sms:+${digits}` : `sms:${digits}`;
}

function extraSelected(extras: PhoneExtra[] | undefined, name: string, index: number): boolean {
  if (!extras?.length) return false;
  const byName = extras.find((e) => (e.name || '').toLowerCase() === name);
  if (byName) return (byName.selected ?? 0) === 1;
  const byIndex = extras[index];
  return Boolean(byIndex && (byIndex.selected ?? 0) === 1);
}

/**
 * Phone "open with" actions — mirrors iOS openPhoneOptions / makePhoneMenu.
 * Always offers Call + SMS; messaging apps only when extras mark them selected.
 */
export function buildPhoneActions(raw: string, extras?: PhoneExtra[]): PhoneAction[] {
  const trimmed = raw.trim();
  const digits = phoneDigits(trimmed);
  if (!digits) return [];

  const actions: PhoneAction[] = [
    { id: 'call', label: 'Direct call', href: telHref(trimmed) },
    { id: 'sms', label: 'Send SMS', href: smsHref(trimmed) },
  ];

  if (extraSelected(extras, 'whatsapp', 0)) {
    actions.push({
      id: 'whatsapp',
      label: 'WhatsApp',
      href: `https://wa.me/${digits}`,
    });
  }
  if (extraSelected(extras, 'signal', 1)) {
    actions.push({
      id: 'signal',
      label: 'Signal',
      href: `https://signal.me/#p/${digits}`,
    });
  }
  if (extraSelected(extras, 'threema', 2)) {
    actions.push({
      id: 'threema',
      label: 'Threema',
      href: `threema://compose/?id=*${encodeURIComponent(trimmed)}`,
    });
  }

  return actions;
}
